import React, { useEffect, useMemo, useState } from "react";
import { CustomDropdown } from "../SpotOrders/spotOrderUi";
import SoMobileFilters from "./SoMobileFilters";
import OrderMobileCardShell from "./OrderMobileCardShell";
import { matchesStatusNorm } from "./orderFilterHelpers";
import AuthService from "../../../api/services/AuthService";
import OrderTablePagination, { useOrderTablePagination } from "./OrderTablePagination";

const parseDec = (val) => {
  if (val == null) return 0;
  if (typeof val === "object" && val.$numberDecimal != null) return Number(val.$numberDecimal) || 0;
  return Number(val) || 0;
};

const formatAmount = (val) => {
  const n = parseDec(val);
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 8 });
};

const formatHistoryDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return String(value);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const normalizeLaunchpadStatus = (status) => {
  switch (String(status || "").toUpperCase()) {
    case "LIVE": return "Live";
    case "UPCOMING": return "Upcoming";
    case "ENDED": return "Ended";
    default: return status || "—";
  }
};

const mapHistoryRow = (item) => {
  const lp = item?.launchpadId || {};
  const tokenSymbol = lp.tokenSymbol || "—";
  const acceptedCurrency = item?.acceptedCurrencyName || lp.acceptedCurrency || "";
  return {
    id: item?._id,
    project: lp.tokenName || tokenSymbol,
    symbol: tokenSymbol,
    category: item?.type || "—",
    price: `${parseDec(lp.tokenPrice)} ${lp.acceptedCurrency || ""}`.trim(),
    totalSupply: `${formatAmount(item?.tokens)} ${tokenSymbol}`,
    achieved: `${formatAmount(item?.amount)} ${acceptedCurrency}`.trim(),
    hardCap: `${formatAmount(item?.tokens)} ${tokenSymbol}`,
    start: formatHistoryDate(item?.createdAt),
    end: "—",
    createdAt: formatHistoryDate(item?.createdAt),
    status: item?.status || normalizeLaunchpadStatus(lp.status),
  };
};

const ORDER_DESKTOP_COL_COUNT = 7;

const CATEGORY_OPTIONS = [
  { value: "all", label: "All" },
  { value: "defi", label: "DeFi" },
  { value: "infrastructure", label: "Infrastructure" },
  { value: "gaming", label: "Gaming" },
  { value: "ai", label: "Ai" },
];

const STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "upcoming", label: "Upcoming" },
  { value: "live", label: "Live" },
  { value: "ended", label: "Ended" },
  { value: "pending", label: "Pending" },
];

/*
const ROWS = [
  {
    id: 1,
    project: "DeFi Protocol X",
    symbol: "DPX",
    category: "DeFi",
    price: "$0.05",
    totalSupply: "1,000,000,000",
    achieved: "$0",
    hardCap: "$2,500,000",
    start: "2026-04-25",
    end: "2026-04-30",
    status: "Upcoming",
  },
  {
    id: 2,
    project: "MetaChain Network",
    symbol: "META",
    category: "Infrastructure",
    price: "$0.08",
    totalSupply: "800,000,000",
    achieved: "$3,350,000",
    hardCap: "$5,000,000",
    start: "2026-04-20",
    end: "2026-04-26",
    status: "Live",
  },
  {
    id: 3,
    project: "GameFi Arena",
    symbol: "GFA",
    category: "Gaming",
    price: "$0.12",
    totalSupply: "500,000,000",
    achieved: "$2,670,000",
    hardCap: "$3,000,000",
    start: "2026-04-15",
    end: "2026-04-22",
    status: "Live",
  },
  {
    id: 4,
    project: "AI Trading Bot",
    symbol: "AIBOT",
    category: "Ai",
    price: "$0.03",
    totalSupply: "2,000,000,000",
    achieved: "$1,500,000",
    hardCap: "$1,500,000",
    start: "2026-04-01",
    end: "2026-04-10",
    status: "Ended",
  },
];
*/

const Launchpad = () => {
  const [category, setCategory] = useState("all");
  const [status, setStatus] = useState("all");
  const [openDropdown, setOpenDropdown] = useState(null);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    let isMounted = true;
    const fetchHistory = async () => {
      try {
        const result = await AuthService.getMyLaunchpadHistory(1, 50);
        if (!isMounted) return;
        if (result?.success && Array.isArray(result?.data)) {
          setRows(result.data.map(mapHistoryRow));
        } else {
          setRows([]);
        }
      } catch (err) {
        if (isMounted) setRows([]);
      }
    };
    fetchHistory();
    return () => { isMounted = false; };
  }, []);

  const parseMoney = (v) => {
    if (typeof v !== "string") return 0;
    const n = Number(v.replace(/[^0-9.]/g, ""));
    return Number.isFinite(n) ? n : 0;
  };

  const calcPct = (achieved, hardCap) => {
    const a = parseMoney(achieved);
    const h = parseMoney(hardCap);
    if (h <= 0) return 0;
    const pct = (a / h) * 100;
    return Math.max(0, Math.min(100, pct));
  };

  useEffect(() => {
    if (!openDropdown) return undefined;
    const onDocClick = (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      if (!t.closest("[data-so-dropdown]")) setOpenDropdown(null);
    };
    document.addEventListener("click", onDocClick, true);
    return () => document.removeEventListener("click", onDocClick, true);
  }, [openDropdown]);

  useEffect(() => {
    if (!openDropdown) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpenDropdown(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openDropdown]);

  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      if (category && category !== "all") {
        if (String(r.category || "").toLowerCase() !== String(category).toLowerCase()) return false;
      }
      if (!matchesStatusNorm(r.status, status)) return false;
      return true;
    });
  }, [rows, category, status]);

  const { page, setPage, totalPages, pagedRows } = useOrderTablePagination(filteredRows);

  const resetFilters = () => {
    setCategory("all");
    setStatus("all");
  };

  const renderToolbar = (stacked, keyPrefix) => {
    const pref = keyPrefix ? `${keyPrefix}-` : "";
    return (
    <div className={`so-toolbar ${stacked ? "so-toolbar--stack" : ""}`} role="search" aria-label="Filter launchpad">
      <div className="so-toolbar__left">
        {/* <CustomDropdown
          id={`${pref}lp-dd-category`}
          label="Category"
          value={category}
          options={CATEGORY_OPTIONS}
          onChange={setCategory}
          menuKey={`${pref}lp-category`}
          openKey={openDropdown}
          onOpenToggle={setOpenDropdown}
        /> */}
        <CustomDropdown
          id={`${pref}lp-dd-status`}
          label="Status"
          value={status}
          options={STATUS_OPTIONS}
          onChange={setStatus}
          menuKey={`${pref}lp-status`}
          openKey={openDropdown}
          onOpenToggle={setOpenDropdown}
        />
      </div>
    </div>
    );
  };

  const renderEmptyBody = () => (
    <tr>
      <td colSpan={ORDER_DESKTOP_COL_COUNT} className="so-table__emptyWrap">
        <div className="so-emptyState" role="status">
          <img src="/images/no-data.svg" alt="" width={120} height={144} className="so-emptyState__img" />
        </div>
      </td>
    </tr>
  );

  const StatusPill = ({ value }) => {
    if (value === "Upcoming") return <span className="so-lpStatus so-lpStatus so-lpStatus--live">{value}</span>;
    if (value === "Live") return <span className="so-lpStatus so-lpStatus so-lpStatus--ended">{value}</span>;
    if (value === "Ended") return <span className="so-lpStatus so-lpStatus so-lpStatus-ended">{value}</span>;
    if (value === "Pending") return <span className="so-status so-status--pending">{value}</span>;
    return <span className="so-badge--closed">{value}</span>;
  };

  return (
    <div className="dashboard_right">
      <div className="dashboard_listing_section2 Overview_mid">
        <div className="listing_left_outer full_width transaction_history_t desktop_view2 so-desktopWrap">
          <main className="so-page" aria-labelledby="so-launchpad-title">
            <h1 id="so-launchpad-title" className="so-page__title">
              Launchpad
            </h1>

            {renderToolbar(false, "desk")}

            <div className="so-tableWrap so-tableWrap--staking">
              <table className="so-table so-table--rowLines so-table--wide">
                <thead>
                  <tr>
                    <th scope="col">Project</th>
                    <th scope="col">Category</th>
                    <th scope="col">
                      Price
                    </th>
                    <th scope="col">
                      Total Supply
                    </th>
                    <th scope="col">
                      Amount / Achieved
                    </th>
                    <th scope="col">Date</th>
                    <th scope="col">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.length === 0
                    ? renderEmptyBody()
                    : pagedRows.map((r) => (
                        <tr key={r.id}>
                          <td>
                            <div className="so-contractCell">
                              <span className="so-contractCell__symbol"><b>{r.project}</b></span>
                              <span className="so-contractCell__time">{r.symbol}</span>
                            </div>
                          </td>
                          <td>
                            <span className="so-badge--closed">{r.category}</span>
                          </td>
                          <td className="so-num">{r.price}</td>
                          <td className="so-num">{r.totalSupply}</td>
                          <td className="so-num">
                            <div className="so-lpAmtWrap">
                              <div>
                                <span className="so-lpAchieved">{r.achieved}</span>{" "}
                                <span className="so-table__td--muted">/ {r.hardCap}</span>
                              </div>
                              <div className="so-lpProgress" aria-label={`Achieved ${calcPct(r.achieved, r.hardCap).toFixed(0)}%`}>
                                <span className="so-lpProgress__bar" aria-hidden="true">
                                  <span className="so-lpProgress__fill" style={{ width: `${calcPct(r.achieved, r.hardCap)}%` }} />
                                </span>
                                {/* <span className="so-lpProgress__pct so-num">{calcPct(r.achieved, r.hardCap).toFixed(0)}%</span> */}
                              </div>
                            </div>
                          </td>
                          <td className="so-num">{r.createdAt}</td>
                         
                          <td>
                            <StatusPill value={r.status} />
                          </td>
                        </tr>
                      ))}
                </tbody>
              </table>
            </div>

            <OrderTablePagination
              page={page}
              totalPages={totalPages}
              onPageChange={setPage}
              label="Launchpad order pages"
            />
          </main>
        </div>

        <div className="order_history_mobile_view so-mobile">
          <h2 className="so-mobile__title">Launchpad</h2>
          <div className="so-mobile__toolbar">
            <SoMobileFilters modalId="mob-launchpad-filters" toolbar={renderToolbar(true, "mob")} onReset={resetFilters} />
          </div>
          {filteredRows.length === 0 ? (
            <div className="so-emptyState so-emptyState--mobile" role="status">
              <img src="/images/no-data.svg" alt="" width={120} height={144} className="so-emptyState__img" />
            </div>
          ) : (
            pagedRows.map((r) => (
              <OrderMobileCardShell key={`m-${r.id}`} desktopColumnCount={ORDER_DESKTOP_COL_COUNT} popupTitle="Launchpad details">
                <div className="so-mobileCard__row">
                  <span>Project</span>
                  <span className="so-mobileCard__stack">
                    <span>{r.project}</span>
                    <span className="so-table__td--muted">{r.symbol}</span>
                  </span>
                </div>
                <div className="so-mobileCard__row">
                  <span>Category</span>
                  <span className="so-badge--closed">{r.category}</span>
                </div>
                <div className="so-mobileCard__row">
                  <span>Price</span>
                  <span className="so-num">{r.price}</span>
                </div>
                <div className="so-mobileCard__row">
                  <span>Total Supply</span>
                  <span className="so-num">{r.totalSupply}</span>
                </div>
                <div className="so-mobileCard__row">
                  <span>Amount / Achieved</span>
                  <span className="so-mobileCard__stack so-lpAmtWrap">
                    <span className="so-num">
                      <span className="so-lpAchieved">{r.achieved}</span>{" "}
                      <span className="so-table__td--muted">/ {r.hardCap}</span>
                    </span>
                    <span className="so-lpProgress so-lpProgress--mobile" aria-label={`Achieved ${calcPct(r.achieved, r.hardCap).toFixed(0)}%`}>
                      <span className="so-lpProgress__bar" aria-hidden="true">
                        <span className="so-lpProgress__fill" style={{ width: `${calcPct(r.achieved, r.hardCap)}%` }} />
                      </span>
                      <span className="so-lpProgress__pct so-num">{calcPct(r.achieved, r.hardCap).toFixed(0)}%</span>
                    </span>
                  </span>
                </div>
                <div className="so-mobileCard__row">
                  <span>Date</span>
                  <span className="so-mobileCard__stack">
                    <span className="so-num">
                      <span className="so-lpDateLbl">Start:</span> {r.start}
                    </span>
                    <span className="so-num">
                      <span className="so-lpDateLbl">End:</span> {r.end}
                    </span>
                  </span>
                </div>
                <div className="so-mobileCard__row">
                  <span>Status</span>
                  <span>
                    <StatusPill value={r.status} />
                  </span>
                </div>
              </OrderMobileCardShell>
            ))
          )}

          <OrderTablePagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            label="Launchpad order pages"
          />
        </div>
      </div>
    </div>
  );
};

export default Launchpad;

